import { CapabilityCoordinatorBase, CapabilityRegistry } from '@trimble/web3d';
import { AlignmentNavigationCapability } from '@trimble/web3d-capabilities';
import {
  AlignmentNavigationStatus,
  StartNavigationOptions,
} from '@technology/web3d-plugin-trimbim';
import {
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
  getTrbModelId,
} from '@trimble/web3d-providers';
import { detectArrayChanges } from '@trimble/utilities';
import { getLogger } from '@trimble/logging';
import { PaneSyncer } from '../PaneSyncer';
import {
  Accessor,
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  getOwner,
  on,
  Owner,
  runWithOwner,
  Setter,
  untrack,
} from 'solid-js';

/**
 * Coordinates alignment navigation across panes.
 *
 * The coordinator owns a single concept: the **desired alignment** — the
 * alignment that every pane currently in path-navigation or cross-section
 * view mode should be navigating.  It is updated from two sources:
 *
 * - External: the data-context's active alignment (via
 *   {@link PathNavigationCoordinator.setActiveAlignmentModelId}, called from
 *   tree selection helpers and view-restoration code).
 * - Internal: when any pane spontaneously starts navigating a new alignment
 *   (e.g. the user picks an alignment in path-nav mode), the coordinator
 *   lifts that alignment into the desired state so other panes follow.
 *
 * Per-capability reactive effects bring each pane onto the desired
 * alignment when the pane is active and metadata is ready.  Position and
 * direction propagation across already-navigating panes is handled by the
 * `syncAlignmentToAlignment` SyncFn driven by `PaneSyncer`.
 */
export class PathNavigationCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private disposeRoot: (() => void) | undefined;

  private readonly setDesiredAlignmentId: Setter<string | undefined>;

  /**
   * Re-entrancy guard: while we are awaiting `startNavigation` on a
   * capability, the underlying tool emits status/modelId updates in
   * sequence (status first, then modelId).  Without this guard the
   * `watchDesiredAlignment` effect would re-fire between the two writes
   * and trigger a second `startNavigation` for the same target.
   */
  private readonly startingNavigation =
    new WeakSet<AlignmentNavigationCapability>();

  /**
   * Current desired alignment model id.  Mirrored into
   * `dataContext.activeItems.alignmentId` by `CapabilityRegistryProvider`.
   */
  public readonly desiredAlignmentModelId: Accessor<string | undefined>;

  constructor(registry: CapabilityRegistry) {
    super(registry);

    const [desiredAlignmentId, setDesiredAlignmentId] = createSignal<
      string | undefined
    >(undefined);
    this.desiredAlignmentModelId = desiredAlignmentId;
    this.setDesiredAlignmentId = setDesiredAlignmentId;

    this.disposeRoot = createRoot((dispose) => {
      const owner = getOwner();

      const alignmentCapabilities = createMemo(() =>
        registry.getPaneCapabilities(AlignmentNavigationCapability)
      );

      createEffect(
        on(alignmentCapabilities, (capabilities, prevCapabilities) => {
          const { newItems } = detectArrayChanges(
            prevCapabilities,
            capabilities
          );

          for (const capability of newItems) {
            this.watchCapability(owner, capability);
          }
        })
      );

      this.watchModelChanges();

      return dispose;
    });
  }

  /**
   * Records the active alignment so the coordinator can auto-start
   * navigation on every pane currently in path-navigation or
   * cross-section view mode (and on panes mounted later in those modes).
   */
  public setActiveAlignmentModelId(modelId: string): void {
    this.setDesiredAlignmentId(modelId);
  }

  private watchCapability(
    owner: Owner | null,
    capability: AlignmentNavigationCapability
  ): void {
    this.watchNavigationStart(owner, capability);
    this.watchDesiredAlignment(owner, capability);
    this.watchDirectionChange(owner, capability);
  }

  // ── Lift spontaneous nav-start into desired state + unload fallback ──

  private watchNavigationStart(
    owner: Owner | null,
    capability: AlignmentNavigationCapability
  ): void {
    runWithOwner(owner, () => {
      createEffect(
        on(
          () => ({
            status: capability.status(),
            modelId: capability.alignmentModelId(),
          }),
          (current, previous) => {
            if (PaneSyncer.isLoadingView()) return;

            if (
              previous?.status === AlignmentNavigationStatus.Navigating &&
              current.status !== AlignmentNavigationStatus.Navigating &&
              capability.isActive() &&
              previous.modelId &&
              !this.isModelLoaded(previous.modelId)
            ) {
              getLogger().debug(
                `[PathNavCoord] navigation lost: model ${previous.modelId} unloaded`
              );
              this.fallbackToNextLoadedAlignment(previous.modelId);
              return;
            }

            if (
              current.status === AlignmentNavigationStatus.Navigating &&
              current.modelId &&
              current.modelId !== this.desiredAlignmentModelId()
            ) {
              this.setDesiredAlignmentId(current.modelId);
            }
          }
        )
      );
    });
  }

  // ── Single reactive effect: bring pane onto the desired alignment ────

  /**
   * Reactive effect that starts navigation on a pane when all conditions
   * are met: a desired alignment exists, the pane is active (path-nav or
   * cross-section view mode), the pane's metadata provider has analyzed
   * the alignment model, and the pane is not already navigating that
   * alignment.
   *
   * Because every dependency is a tracked signal, SolidJS automatically
   * re-runs the effect whenever any of them change — covering use cases:
   *  - a pane switches to path-nav/cross-section mode (isActive flip),
   *  - metadata finishes analyzing,
   *  - a new desired alignment lands from the data context,
   *  - another pane starts navigating a different alignment (which
   *    `watchNavigationStart` lifts into the desired state).
   *
   * If another pane is already navigating the desired alignment, the
   * effect pulls its state (chainage, direction, optional camera) so the
   * target pane comes online at the correct position — independent of
   * the PaneSyncer pipeline that follows.
   */
  private watchDesiredAlignment(
    owner: Owner | null,
    capability: AlignmentNavigationCapability
  ): void {
    runWithOwner(owner, () => {
      createEffect(() => {
        if (PaneSyncer.isLoadingView()) return;

        const desiredModelId = this.desiredAlignmentModelId();
        if (!desiredModelId || !capability.isActive()) {
          return;
        }

        if (
          capability.status() === AlignmentNavigationStatus.Navigating &&
          capability.alignmentModelId() === desiredModelId
        ) {
          return;
        }

        if (!this.isMetadataReady(capability, desiredModelId)) {
          return;
        }

        untrack(() => {
          void this.startNavigationOnTarget(capability, desiredModelId);
        });
      });
    });
  }

  private async startNavigationOnTarget(
    target: AlignmentNavigationCapability,
    modelId: string
  ): Promise<void> {
    if (this.startingNavigation.has(target)) {
      return;
    }
    this.startingNavigation.add(target);
    try {
      const leader = this.findNavigatingLeader(target, modelId);

      if (leader) {
        const leaderState = await leader.getNavigationState();
        const options = this.buildNavigationOptions(
          leader,
          target,
          leaderState
        );
        await target.startNavigation(modelId, options);

        // forceLookForward (set for cross-section starts in
        // AlignmentNavigationCapability.startNavigation) resets the
        // tool's internal direction.  Re-apply the leader's direction
        // explicitly so cross-section targets land oriented like the
        // leader regardless of how the multipane sync pipeline races.
        if (leaderState?.direction !== undefined) {
          target.applyDirection(leaderState.direction);
        }
        return;
      }

      await target.startNavigation(modelId, {
        crossSectionEnabled: target.crossSectionEnabled(),
      });
    } catch (error) {
      getLogger().debug('[PathNavCoord] startNavigationOnTarget failed', error);
    } finally {
      this.startingNavigation.delete(target);
    }
  }

  private findNavigatingLeader(
    exclude: AlignmentNavigationCapability,
    modelId: string
  ): AlignmentNavigationCapability | undefined {
    return this.registry
      .getPaneCapabilities(AlignmentNavigationCapability)
      .find(
        (cap) =>
          cap !== exclude &&
          cap.isActive() &&
          cap.status() === AlignmentNavigationStatus.Navigating &&
          cap.alignmentModelId() === modelId
      );
  }

  /**
   * Returns true if the pane's {@link TrajectoryMetadataProvider} has
   * analyzed metadata for the given alignment model.
   *
   * Reading the provider's `items()` signal inside a `createEffect`
   * establishes a reactive dependency so the effect re-runs when
   * analysis completes.
   */
  private isMetadataReady(
    target: AlignmentNavigationCapability,
    modelId: string
  ): boolean {
    const metadataId = getTrbModelId(modelId) ?? modelId;
    const containers = this.registry.paneContainers();
    for (const container of containers) {
      const cap = container.get(AlignmentNavigationCapability)();
      if (cap === target) {
        const metadataProvider = container.get(TrajectoryMetadataProvider)();
        metadataProvider?.items();
        return metadataProvider?.getById(metadataId) !== undefined;
      }
    }
    return false;
  }

  // ── Propagate direction flips to cross-section targets ──────────────

  /**
   * The trimbim tool only flips `_direction` (and dispatches
   * `AlignmentMarkerMoved`) in `orbitUpCallback` — i.e. at mouse-up at
   * the end of an orbit gesture — when there is no further camera
   * motion.  `PaneSyncer` therefore cannot detect a pure orbit-end
   * direction flip and `syncAlignmentToAlignment` is not re-triggered.
   *
   * This dedicated effect closes that gap: when any active+navigating
   * capability's `direction` signal changes, push the new value to
   * every other cross-section pane.  Path-nav targets own their
   * direction via their own camera and are intentionally skipped.
   */
  private watchDirectionChange(
    owner: Owner | null,
    capability: AlignmentNavigationCapability
  ): void {
    runWithOwner(owner, () => {
      createEffect(
        on(capability.direction, (direction) => {
          if (PaneSyncer.isLoadingView()) return;
          if (capability.status() !== AlignmentNavigationStatus.Navigating) {
            return;
          }

          const allCapabilities = this.registry.getPaneCapabilities(
            AlignmentNavigationCapability
          );

          for (const target of allCapabilities) {
            if (
              target === capability ||
              !target.isActive() ||
              target.status() !== AlignmentNavigationStatus.Navigating ||
              !target.crossSectionEnabled() ||
              target.direction() === direction
            ) {
              continue;
            }
            target.applyDirection(direction);
          }
        })
      );
    });
  }

  // ── Build StartNavigationOptions for a target pane ───────────────────

  private buildNavigationOptions(
    source: AlignmentNavigationCapability,
    target: AlignmentNavigationCapability,
    sourceState: StartNavigationOptions | undefined
  ): StartNavigationOptions {
    const targetCrossSection = target.crossSectionEnabled();
    const bothPathNavigation =
      !source.crossSectionEnabled() && !targetCrossSection;

    if (!sourceState) {
      return { crossSectionEnabled: targetCrossSection };
    }

    return {
      crossSectionEnabled: targetCrossSection,
      section: sourceState.section,
      web3dChainage: sourceState.web3dChainage,
      direction: sourceState.direction,
      ...(bothPathNavigation && {
        cameraPosition: sourceState.cameraPosition,
        cameraQuaternion: sourceState.cameraQuaternion,
      }),
    };
  }

  private isModelLoaded(modelId: string): boolean {
    return this.registry
      .getPaneCapabilities(TrimBimCapabilityProvider)
      .some((p) => p.items().some((m) => m.modelId === modelId));
  }

  private watchModelChanges(): void {
    const totalModelCount = createMemo(() =>
      this.registry
        .getPaneCapabilities(TrimBimCapabilityProvider)
        .reduce((sum, p) => sum + p.items().length, 0)
    );

    createEffect(
      on(totalModelCount, (count, prevCount) => {
        if (count !== (prevCount ?? 0)) {
          this.redrawNavigatingPanes();
        }
      })
    );
  }

  private redrawNavigatingPanes(): void {
    const containers = this.registry.paneContainers();

    for (const container of containers) {
      const cap = container.get(AlignmentNavigationCapability)();
      if (cap?.status() === AlignmentNavigationStatus.Navigating) {
        void container.viewerWrapper()?.viewer.waitForRender();
      }
    }
  }

  // ── Fallback when the active alignment model is unloaded ─────────────

  private fallbackToNextLoadedAlignment(unloadedModelId?: string): void {
    const trbProviders = this.registry.getPaneCapabilities(
      TrimBimCapabilityProvider
    );

    const nextModel = trbProviders
      .flatMap((p) => p.items())
      .find((m) => m.modelId !== unloadedModelId);
    if (nextModel) {
      this.setDesiredAlignmentId(nextModel.modelId);
    } else {
      this.setDesiredAlignmentId(undefined);
    }
  }

  public cleanup(): Promise<void> {
    this.disposeRoot?.();
    this.disposeRoot = undefined;
    return Promise.resolve();
  }
}
